import React, { useMemo, useState } from "react";
import TableV2 from "../../../components/TableV2";
import TableActions from "../../../components/TableActions";
import TitleButtons from "../../../components/TitleButtons";
import { useNavigate } from "react-router-dom";
import { generatePdfReport } from "../../../utils/pdf/generatePdfReport";
import { useProdutosDisponiveis } from "./produtoData";

const SuprimentosProdutos: React.FC = () => {
  const navigate = useNavigate();
  const { produtos, loading } = useProdutosDisponiveis();

  // --- helpers de formatação de moeda ---
  const currencySymbol = (moeda?: string) => {
    const m = String(moeda || "")
      .trim()
      .toUpperCase();
    if (m === "USD") return "$";
    if (m === "EUR") return "€";
    if (m === "BRL") return "R$";
    return ""; // caso outra moeda ou vazio
  };

  const formatCostWithSymbol = (
    valor: number | string | null | undefined,
    moeda?: string
  ) => {
    if (
      valor === null ||
      valor === undefined ||
      valor === "" ||
      Number.isNaN(Number(valor))
    ) {
      return "N/A";
    }
    const sym = currencySymbol(moeda);
    // não forço locale aqui para não alterar seu comportamento atual; só prefixo o símbolo
    return `${sym ? sym + " " : ""}${valor}`;
  };

  const headers = useMemo(
    () => [
      "Código",
      "NCM",
      "Nome do Produto",
      "Custo Unitário",
      "Moeda", // <--- NOVO
      "Classificação",
      "Posição",
    ],
    []
  );

  const columnWidths = useMemo(
    () => ["100px", "60px", "270px", "120px", "70px", "20px", "10px"], // + "Moeda"
    []
  );

  const rows = useMemo(
    () =>
      produtos.map((p) => [
        p.codigo,
        p.ncm,
        p.nome,
        formatCostWithSymbol(p.custo_unitario, p.moeda), // custo com símbolo
        (p.moeda || "").toUpperCase() || "—", // coluna Moeda
        p.classificacao,
        p.posicao,
      ]),
    [produtos]
  );

  const [selectedColumns, setSelectedColumns] = useState<boolean[]>(
    headers.map(() => true)
  );

  const [selectedProduct, setSelectedProduct] = useState<any | null>(null);

  const enabledColumnIndexes = useMemo(
    () =>
      selectedColumns
        .map((checked, i) => (checked ? i : -1))
        .filter((i) => i !== -1),
    [selectedColumns]
  );

  // Helpers p/ PDF seguro (quando gera com todas as linhas passadas ao Table)
  const firstCode = rows.length ? rows[0][0] : "—";
  const lastCode = rows.length ? rows[rows.length - 1][0] : "—";
  const nextCode = `PRD001${(produtos.length + 1).toString().padStart(2, "0")}`;

  return (
    <div>
      <TitleButtons fullBleedLine title="Suprimentos - Produtos" buttons={[]} />
      <TableActions
        buttons={[
          {
            label: "Gerar Relatório",
            onClick: () =>
              generatePdfReport({
                reportTitle: "Relatório de Produtos - Suprimentos",
                fileNamePrefix: "Suprimentos-Produtos",
                subtitle: "Informações dos produtos registrados",
                headers,
                rows,
                custoColIndex: 3, // permanece o mesmo
                enabledColumns: enabledColumnIndexes,
                footerLines: [
                  rows.length
                    ? `Códigos Utilizados: ${firstCode} - ${lastCode}`
                    : "Sem produtos",
                  `Próximo Código Disponível: ${nextCode}`,
                ],
              }),
            variant: "secondary",
            dropdownOptions: headers.map((h, i) => ({
              label: h,
              checked: selectedColumns[i],
            })),
            onOptionChange: (index, checked) => {
              const updated = [...selectedColumns];
              updated[index] = checked;
              setSelectedColumns(updated);
            },
          },
          {
            label: "Registrar Produto / Adicionar ao Estoque",
            onClick: () =>
              navigate("/suprimentos/produtos/registrar-produto-v2"),
            variant: "primary",
          },
        ]}
      />
      <TableV2
        enableZebra
        hoverHighlight
        headers={headers}
        rows={rows}
        columnWidths={columnWidths}
        onDoubleClick={(_viewIndex, rowData) => {
          const codigoIdx = headers.indexOf("Código");
          const codigo = String(rowData[codigoIdx] ?? "");
          if (codigo) {
            navigate(`/suprimentos/produtos/${encodeURIComponent(codigo)}`);
          }
        }}
        // atualizado para 7 colunas
        responsiveWidths={{
          1300: {
            1: "100px", // Código
            2: "0px", // NCM
            3: "270px", // Nome
            4: "140px", // Custo Unitário
            5: "70px", // Moeda
            6: "0px", // Classificação
            7: "60px", // Posição
          },
          1200: {
            1: "100px",
            2: "0px",
            3: "270px",
            4: "140px",
            5: "70px",
            6: "0px",
            7: "60px",
          },
        }}
      />

      {loading && (
        <div style={{ padding: 16, opacity: 0.7, fontSize: 14 }}>
          Carregando produtos…
        </div>
      )}
    </div>
  );
};

export default SuprimentosProdutos;
